import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import api from "../Utility/api";
import { useAuth } from "../Utility/AuthContext";
import { availableOptions, chooseOption } from "../Utility/storyFormat";
import Seo from "../Components/Seo";
const API = process.env.REACT_APP_API_URL || "";
const initialState = { node: 1, traits: [], text: "" };
function parseTraits(value) {
  // Older bookmarks were double encoded. Continue to support them.
  for (let i = 0; i < 2 && typeof value === "string"; i++)
    value = JSON.parse(value);
  return Array.isArray(value) ? value : [];
}
export default function StoryPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [rows, setRows] = useState([]);
  const [state, setState] = useState(initialState);
  const [bookmark, setBookmark] = useState(null);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    setStory(null);
    setError("");
    setState(initialState);
    setBookmark(null);
    setNotice("");
    setActionError("");
    api
      .get(`${API}/api/stories/${id}`)
      .then(({ data }) => {
        if (!active) return;
        const content = JSON.parse(data.story_text);
        setRows(content);
        setStory(data);
        if (data.current_text != null) {
          try {
            setBookmark({
              text: data.current_text,
              node: Number(data.current_node),
              traits: parseTraits(data.trait_list || []),
            });
          } catch {
            /* An invalid legacy bookmark should not prevent reading. */
          }
        }
      })
      .catch(() => {
        if (active)
          setError("This story could not be loaded. It may have been removed.");
      });
    return () => {
      active = false;
    };
  }, [id, user?.id]);
  const action = async (kind) => {
    setBusy(true);
    setActionError("");
    setNotice("");
    try {
      if (kind === "bookmark") {
        await api.post(`${API}/api/stories/bookmark`, {
          story_id: id,
          current_node: state.node,
          current_text: state.text,
          trait_list: state.traits,
        });
        setBookmark({ ...state });
        setNotice("Your place is saved.");
      } else {
        await api.put(`${API}/api/stories/${kind}/${id}`, {});
        setStory((current) => ({
          ...current,
          [kind === "like" ? "liked_by_user" : "reported_by_user"]: true,
        }));
        setNotice(
          kind === "like"
            ? "A little love for the storyteller. Thank you!"
            : "Report received. Thank you for helping the community.",
        );
      }
    } catch (err) {
      setActionError(
        err.response?.data?.error ||
          "Unable to complete that action. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };
  if (error)
    return (
      <Container sx={{ mt: 5 }}>
        <Seo title="Story unavailable" noindex />
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  if (!story)
    return (
      <Box textAlign="center" p={8}>
        <CircularProgress aria-label="Loading story" />
      </Box>
    );
  const options = availableOptions(rows, state.node, state.traits);
  return (
    <Container maxWidth="md" sx={{ pt: { xs: 4, md: 6 } }}>
      <Seo
        title={story.story_name}
        description={
          story.story_desc ||
          `Play ${story.story_name}, an interactive story by ${story.author}. Every choice leads somewhere new.`
        }
        type="article"
      />
      <Button component={Link} to="/dashboard" sx={{ mb: 3 }}>
        ← Back to the library
      </Button>
      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 6 } }}>
        <span className="eyebrow">An adventure of your choosing</span>
        <Typography
          variant="h2"
          component="h1"
          sx={{ overflowWrap: "anywhere" }}
        >
          {story.story_name}
        </Typography>
        <Typography color="text.secondary" mt={1}>
          Written by{" "}
          <Link to={`/profile/${encodeURIComponent(story.author)}`}>
            {story.author}
          </Link>
        </Typography>
        {story.created_by === user?.id && (
          <Button
            component={Link}
            to={`/edit/${id}`}
            variant="outlined"
            size="small"
            sx={{ mt: 2 }}
          >
            Edit & export story
          </Button>
        )}
        <Divider sx={{ my: 4 }} />
        <Typography
          sx={{
            fontFamily: "Georgia, serif",
            fontSize: { xs: 18, sm: 21 },
            lineHeight: 1.9,
            whiteSpace: "pre-wrap",
            mb: 4,
          }}
          aria-live="polite"
        >
          {state.text ||
            story.story_desc ||
            "Every adventure begins with a choice. Make yours."}
        </Typography>
        <Stack gap={1.5}>
          {options.map((option, index) => (
            <Button
              variant="outlined"
              key={index}
              onClick={() => {
                setState(chooseOption(option, state.traits));
                setNotice("");
              }}
              fullWidth
              sx={{
                justifyContent: "space-between",
                textAlign: "left",
                py: 2,
                px: 3,
              }}
            >
              <span>{option.optionText}</span>
              <span aria-hidden="true">↗</span>
            </Button>
          ))}
        </Stack>
        {!options.length && (
          <Box sx={{ my: 3 }}>
            <Typography variant="h5">
              {state.node === 0 ? "The end." : "This path goes no further."}
            </Typography>
            <Typography color="text.secondary">
              There may be another ending waiting for you.
            </Typography>
          </Box>
        )}
        <Stack direction="row" gap={1} mt={3} flexWrap="wrap">
          {state.traits.map((tag) => (
            <Chip key={tag} label={tag} variant="outlined" size="small" />
          ))}
        </Stack>
        <Divider sx={{ my: 3 }} />
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {notice}
          </Alert>
        )}
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button
            startIcon={<FavoriteBorderIcon />}
            disabled={!user || busy || story.liked_by_user}
            onClick={() => action("like")}
          >
            {story.liked_by_user ? "Loved" : "Like"}
          </Button>
          <Button
            startIcon={<BookmarkBorderIcon />}
            disabled={!user || busy}
            onClick={() => action("bookmark")}
          >
            Save place
          </Button>
          <Button
            disabled={!bookmark}
            onClick={() => {
              setState({ ...bookmark });
              setNotice("Bookmark restored.");
            }}
          >
            Load place
          </Button>
          <Button
            onClick={() => {
              setState(initialState);
              setNotice("");
            }}
          >
            Restart
          </Button>
          <Button
            color="error"
            disabled={!user || busy || story.reported_by_user}
            onClick={() => action("report")}
          >
            {story.reported_by_user ? "Reported" : "Report"}
          </Button>
        </Stack>
        {!user && (
          <Typography variant="body2" color="text.secondary" mt={2}>
            <Link to="/login">Sign in</Link> to save your place and support the
            author.
          </Typography>
        )}
      </Paper>
    </Container>
  );
}
